/* =====================================================================
   assets/soi-ui.js · Spirit of India UI layer · M0 (05 D4, §7.4)
   Custom elements: <soi-tabs>, <soi-dialog-trigger>, <soi-menu-drawer>, <soi-mega>, <soi-marquee>, <soi-toasts>
   Globals: window.soiToast(message, options), window.soiUI (helpers), header state (sentinel IO, hide on scroll),
   product-card quick add (form[data-soi-quick-add]) through Studio's <cart-drawer> contract.
   Progressive enhancement: links, <details>, radios and forms work without this file; JS adds state and motion.
   No frameworks, no layout reads in scroll handlers, passive listeners, no setInterval animation.
   ===================================================================== */
(() => {
  'use strict';

  const STR = window.soiStrings || {};
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const anyLayerOpen = () =>
    !!document.querySelector('dialog[open], cart-drawer.active, [data-soi-layer-open="true"], details[data-soi-layer][open]');

  /* ---------------------------------------------------------------- <soi-tabs> (C38, ARIA tabs pattern) */
  class SoiTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')].filter((t) => t.closest('soi-tabs') === this);
      if (!this.tabs.length) return;
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i, true));
        tab.addEventListener('keydown', (e) => this.onKey(e, i));
      });
      const current = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      this.select(current < 0 ? 0 : current, false);
    }
    onKey(e, i) {
      const last = this.tabs.length - 1;
      let next = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = last;
      if (next === null) return;
      e.preventDefault();
      this.select(next, true);
      this.tabs[next].focus();
    }
    select(index, user) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      this.selected = index;
      if (user) this.dispatchEvent(new CustomEvent('soi:tab-change', { bubbles: true, detail: { index } }));
    }
  }
  define('soi-tabs', SoiTabs);

  /* ---------------------------------------------------------------- dialogs (C33, native <dialog>) */
  const openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog._soiOpener = opener || document.activeElement;
    dialog.showModal();
    const first = dialog.querySelector('[autofocus], .soi-dialog__close');
    if (first) first.focus({ preventScroll: true });
    dialog.dispatchEvent(new CustomEvent('soi:dialog-open', { bubbles: true }));
  };
  const closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    dialog.close();
  };
  document.addEventListener('click', (e) => {
    const closer = e.target.closest('[data-soi-dialog-close]');
    if (closer) {
      closeDialog(closer.closest('dialog'));
      return;
    }
    const dialog = e.target.closest('dialog.soi-dialog');
    // backdrop click: the event target is the dialog itself (content sits in child elements)
    if (dialog && e.target === dialog && !dialog.hasAttribute('data-soi-strict')) closeDialog(dialog);
  });
  document.addEventListener('close', (e) => {
    const d = e.target;
    if (d instanceof HTMLDialogElement && d._soiOpener && document.contains(d._soiOpener)) {
      d._soiOpener.focus({ preventScroll: true });
    }
  }, true);

  class SoiDialogTrigger extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('button, a') || this;
      btn.setAttribute('aria-haspopup', 'dialog');
      btn.addEventListener('click', (e) => {
        const dialog = document.getElementById(this.dataset.target);
        if (!dialog) return;
        e.preventDefault();
        openDialog(dialog, btn);
      });
    }
  }
  define('soi-dialog-trigger', SoiDialogTrigger);

  /* ---------------------------------------------------------------- <soi-menu-drawer> (C11, opens a left <dialog>) */
  class SoiMenuDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.toggle = this.querySelector('[data-soi-menu-toggle]');
      if (!this.dialog || !this.toggle) return;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.addEventListener('click', () => this.open());
      this.dialog.addEventListener('close', () => {
        this.toggle.setAttribute('aria-expanded', 'false');
        if (STR.menuOpen) this.toggle.setAttribute('aria-label', STR.menuOpen);
      });
    }
    open() {
      openDialog(this.dialog, this.toggle);
      this.toggle.setAttribute('aria-expanded', 'true');
      if (STR.menuClose) this.toggle.setAttribute('aria-label', STR.menuClose);
    }
  }
  define('soi-menu-drawer', SoiMenuDrawer);

  /* ---------------------------------------------------------------- <soi-mega> (C11 mega panel) */
  class SoiMega extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-soi-mega-trigger]');
      this.panel = this.querySelector('[data-soi-mega-panel]');
      if (!this.trigger || !this.panel) return;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel.hidden = true;
      this.trigger.addEventListener('click', (e) => { e.preventDefault(); this.isOpen ? this.close(true) : this.open(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen) { e.stopPropagation(); this.close(true); } });
      this.addEventListener('focusout', (e) => { if (this.isOpen && !this.contains(e.relatedTarget)) this.close(false); });
      this.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || !finePointer()) return;
        clearTimeout(this._closeT);
        this._openT = setTimeout(() => this.open(), 120);
      });
      this.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(this._openT);
        this._closeT = setTimeout(() => this.close(false), 300);
      });
      document.addEventListener('click', (e) => { if (this.isOpen && !this.contains(e.target)) this.close(false); });
    }
    get isOpen() { return this.trigger.getAttribute('aria-expanded') === 'true'; }
    open() {
      this.panel.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.setAttribute('data-soi-layer-open', 'true');
    }
    close(returnFocus) {
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.removeAttribute('data-soi-layer-open');
      if (returnFocus) this.trigger.focus();
    }
  }
  define('soi-mega', SoiMega);

  /* ---------------------------------------------------------------- <soi-marquee> (C13) */
  class SoiMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.soi-marquee__track');
      this.root = this.querySelector('.soi-marquee') || this;
      this.btn = this.querySelector('[data-soi-marquee-toggle]');
      if (!this.track) return;
      const setDur = () => {
        const speed = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--marquee-speed')) || 0;
        const group = this.track.firstElementChild;
        if (!group || !speed) { this.root.style.setProperty('--marquee-dur', '0s'); return; }
        this.root.style.setProperty('--marquee-dur', `${Math.round(group.scrollWidth / speed)}s`);
      };
      if ('ResizeObserver' in window) new ResizeObserver(setDur).observe(this.track);
      setDur();
      if (this.btn) {
        this.btn.addEventListener('click', () => {
          const paused = this.root.classList.toggle('is-paused');
          this.btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
          this.btn.setAttribute('aria-label', paused ? (STR.marqueePlay || 'Play') : (STR.marqueePause || 'Pause'));
        });
      }
    }
  }
  define('soi-marquee', SoiMarquee);

  /* ---------------------------------------------------------------- <soi-toasts> + window.soiToast (C36) */
  class SoiToasts extends HTMLElement {
    connectedCallback() {
      this.status = this.querySelector('[data-soi-toasts="status"]') || this;
      this.alert = this.querySelector('[data-soi-toasts="alert"]') || this;
    }
    show(message, opts = {}) {
      const type = opts.type === 'error' ? 'error' : 'status';
      this.querySelectorAll('.soi-toast').forEach((t) => this.dismiss(t, true));
      const toast = document.createElement('div');
      toast.className = `soi-toast soi-toast--${type}`;
      const text = document.createElement('p');
      text.className = 'soi-toast__text';
      text.textContent = message;
      toast.append(text);
      if (opts.action && opts.action.href) {
        const a = document.createElement('a');
        a.className = 'soi-toast__action';
        a.href = opts.action.href;
        a.textContent = opts.action.label;
        toast.append(a);
      }
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'soi-toast__close';
      close.setAttribute('aria-label', STR.toastClose || 'Close');
      close.innerHTML = '<svg width="20" height="20" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" focusable="false"><path d="M208.49,191.51a12,12,0,0,1-17,17L128,145,64.49,208.49a12,12,0,0,1-17-17L111,128,47.51,64.49a12,12,0,0,1,17-17L128,111l63.51-63.52a12,12,0,0,1,17,17L145,128Z"/></svg>';
      close.addEventListener('click', () => this.dismiss(toast));
      toast.append(close);
      (type === 'error' ? this.alert : this.status).append(toast);
      if (type !== 'error') {
        const ms = opts.timeout || 5000;
        let timer = setTimeout(() => this.dismiss(toast), ms);
        const pause = () => clearTimeout(timer);
        const resume = () => { clearTimeout(timer); timer = setTimeout(() => this.dismiss(toast), 2000); };
        toast.addEventListener('pointerenter', pause);
        toast.addEventListener('pointerleave', resume);
        toast.addEventListener('focusin', pause);
        toast.addEventListener('focusout', resume);
      }
      return toast;
    }
    dismiss(toast, instant) {
      if (!toast || !toast.isConnected) return;
      if (instant || reduceMotion()) { toast.remove(); return; }
      toast.classList.add('is-leaving');
      toast.addEventListener('transitionend', () => toast.remove(), { once: true });
      setTimeout(() => toast.remove(), 400);
    }
  }
  define('soi-toasts', SoiToasts);
  window.soiToast = (message, opts) => {
    const host = document.querySelector('soi-toasts');
    return host && host.show ? host.show(message, opts) : null;
  };

  /* ---------------------------------------------------------------- header state (C11): solid after the sentinel, hide on scroll ≤ 989 */
  const initHeader = () => {
    const header = document.querySelector('[data-soi-header]');
    if (!header) return;
    const sentinel = document.querySelector('[data-soi-header-sentinel]');
    if (sentinel && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        header.classList.toggle('is-solid', !entry.isIntersecting);
      }).observe(sentinel);
    } else {
      header.classList.add('is-solid');
    }
    const settings = window.soiSettings || {};
    if (!settings.headerHideOnScroll) return;
    const narrow = window.matchMedia('(max-width: 989.98px)');
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      ticking = false;
      const y = window.scrollY;
      const hide = narrow.matches && !reduceMotion() && y > 400 && y > lastY && !anyLayerOpen();
      header.classList.toggle('is-hidden', hide);
      lastY = y;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
  };

  /* ---------------------------------------------------------------- product card quick add (C15, 04 §5.3) */
  const sectionsFor = () => {
    const drawer = document.querySelector('cart-drawer');
    return drawer && typeof drawer.getSectionsToRender === 'function'
      ? drawer.getSectionsToRender().map((s) => s.id) : ['cart-icon-bubble'];
  };
  const addToCart = async (items, opts = {}) => {
    const drawer = document.querySelector('cart-drawer');
    const body = { items, sections: sectionsFor().join(','), sections_url: window.location.pathname };
    const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw Object.assign(new Error(data.description || data.message || 'cart'), { data });
    if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
      // Studio's product-form removes .is-empty before rendering; without it cart-drawer.js traps focus on a
      // node that no longer exists (M1 fix)
      drawer.classList.remove('is-empty');
      drawer.renderContents({ id: items[0] && items[0].id, sections: data.sections });
    } else if (data.sections && data.sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        const doc = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
        const inner = doc.querySelector('.shopify-section');
        if (inner) bubble.innerHTML = inner.innerHTML;
      }
      if (opts.name && STR.added) window.soiToast(STR.added.replace('[name]', opts.name));
    }
    document.dispatchEvent(new CustomEvent('soi:cart:added', { detail: { items, response: data } }));
    if (window.publish && window.PUB_SUB_EVENTS) window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'soi', cartData: data });
    return data;
  };

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-soi-quick-add]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const err = form.querySelector('.soi-card__error');
    const fd = new FormData(form);
    const properties = {};
    for (const [k, v] of fd.entries()) {
      const m = k.match(/^properties\[(.+)\]$/);
      if (m) properties[m[1]] = v;
    }
    const item = { id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1), properties };
    const label = btn && btn.querySelector('.soi-btn__label');
    const original = label ? label.textContent : '';
    if (btn) {
      btn.style.minInlineSize = `${btn.offsetWidth}px`;
      btn.setAttribute('aria-busy', 'true');
      if (label && STR.loading) label.textContent = STR.loading;
    }
    if (err) err.hidden = true;
    try {
      const card = form.closest('[data-soi="card"]');
      const name = card ? (card.querySelector('.soi-card__name') || {}).textContent : '';
      await addToCart([item], { name: (name || '').trim() });
    } catch (ex) {
      if (err) { err.textContent = STR.cardError || STR.atcError || ''; err.hidden = false; }
      window.soiToast(STR.atcError || 'Error', { type: 'error' });
    } finally {
      if (btn) {
        btn.removeAttribute('aria-busy');
        btn.style.minInlineSize = '';
        if (label) label.textContent = original;
      }
    }
  });

  /* ---------------------------------------------------------------- in-page anchors (01 §3.1 #4)
     After a jump like „Finde dein Ritual" → #needs, focus moves to the target's first heading (tabindex -1,
     no second scroll), so keyboard and screen-reader users land on the section title, not on the section box.
     Footnote markers and links whose own handler already took over (defaultPrevented) are left alone. */
  document.addEventListener('click', (e) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const link = e.target.closest && e.target.closest('a[href*="#"]');
    if (!link || link.classList.contains('soi-fn__link')) return;
    const url = new URL(link.href, window.location.href);
    if (url.pathname !== window.location.pathname || url.search !== window.location.search || url.hash.length < 2) return;
    let target = null;
    try { target = document.getElementById(decodeURIComponent(url.hash.slice(1))); } catch (err) { target = null; }
    if (!target || target.closest('dialog, [role="dialog"]')) return;
    setTimeout(() => {
      if (e.defaultPrevented && !target.contains(document.activeElement) && document.activeElement !== document.body) return;
      const heading = target.matches('h1, h2, h3') ? target : target.querySelector('h2, h1, h3');
      const dest = heading || target;
      if (!dest.hasAttribute('tabindex') && !dest.matches('a[href], button, input, select, textarea')) dest.setAttribute('tabindex', '-1');
      dest.focus({ preventScroll: true });
    }, 0);
  });

  /* A hash that points at a closed <details> (e.g. a card's „Gefahrenhinweise ansehen" → PDP #sicherheit) opens it */
  const openHashDetails = () => {
    let el = null;
    try { el = window.location.hash.length > 1 ? document.getElementById(decodeURIComponent(window.location.hash.slice(1))) : null; } catch (err) { el = null; }
    const d = el && (el.matches('details') ? el : el.closest('details'));
    if (d && !d.open) {
      d.open = true;
      requestAnimationFrame(() => el.scrollIntoView({ block: 'start', behavior: 'auto' }));
    }
  };
  window.addEventListener('hashchange', openHashDetails);

  window.soiUI = { openDialog, closeDialog, addToCart, reduceMotion, finePointer };

  /* ---------------------------------------------------------------- boot */
  const boot = () => {
    initHeader();
    openHashDetails();
    window.addEventListener('offline', () => STR.offline && window.soiToast(STR.offline, { type: 'error' }));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
